import { useLayoutEffect, useMemo, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useShallow } from "zustand/react/shallow";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import {
  useCreatePageStore,
  CreatePageState,
  CreatePageAction,
} from "@/stores/createPageStore";
import { useCreatePageActions } from "./useCreatePageActions";
import { useCreatePageEffects } from "./useCreatePageEffects";
import { CREATE_RESTORATION_DEADLINE_MS } from "./effects/useInitialization";
import {
  EXACT_SESSION_RESTORE_RETRY_MESSAGE,
  useAgentSession,
} from "./hooks/useAgentSession";
import { createAuthoritativeCreateRestoreTargetKey } from "./utils/urlParamHelpers";
import { readStoredAgentSessionId } from "./utils/agentSessionId";

export { CREATE_RESTORATION_DEADLINE_MS };

// The create page is chat-only: the legacy 4-step form mode was removed, so
// the agent session is always active and actions run in chat mode.
export function useCreatePage() {
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();
  const freshStartRequested = searchParams.get("new") === "1";
  const requestedSessionId = freshStartRequested
    ? null
    : searchParams.get("session_id");
  const authoritativeRestoreTargetKey = useMemo(
    () =>
      createAuthoritativeCreateRestoreTargetKey({
        modifyParam: searchParams.get("modify"),
        ticketIdParam: searchParams.get("ticket_id"),
        galleryDesignParam: searchParams.get("gallery_design"),
      }),
    // The Next.js search params object is read-only but its identity is not a
    // useful dependency. Its serialized value changes for every URL target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParamsString]
  );
  const agentSessionRestorationPending = useAgentSession(
    true,
    authoritativeRestoreTargetKey,
    requestedSessionId,
    freshStartRequested
  );

  // Use a selector to get only state properties, excluding actions
  // Pick only the state properties from CreatePageState to avoid conflicts with action methods
  // Use useShallow to cache the result and prevent infinite loops by doing shallow comparison
  // useShallow ensures that a new object is only created when the underlying store values actually change
  const state = useCreatePageStore(
    useShallow(
      (store): CreatePageState => ({
        form: store.form,
        generation: store.generation,
        ui: store.ui,
        ticket: store.ticket,
        agent: store.agent,
      })
    )
  );
  // Create a dispatch adapter for effects that still use dispatch
  const dispatch = useMemo(
    () => (action: CreatePageAction) => {
      const store = useCreatePageStore.getState();
      // Map action types to store methods
      switch (action.type) {
        case "UPDATE_FORM_DATA":
          store.updateFormData(action.payload);
          break;
        case "RESET_FORM":
          store.resetForm();
          break;
        case "SET_VALIDATION_ERROR":
          store.setValidationError(action.payload);
          break;
        case "SET_UPLOADED_FRONT_IMAGE":
          // Convert single file to array for backward compatibility
          store.setUploadedFrontImages(action.payload ? [action.payload] : []);
          break;
        case "SET_UPLOADED_BACK_IMAGE":
          // Convert single file to array for backward compatibility
          store.setUploadedBackImages(action.payload ? [action.payload] : []);
          break;
        case "CLEAR_UPLOADED_IMAGES":
          store.clearUploadedImages();
          break;
        case "SET_STEP":
          store.setStep(action.payload);
          break;
        case "NEXT_STEP":
          store.nextStep();
          break;
        case "PREV_STEP":
          store.prevStep();
          break;
        case "APPROVE_FRONT_DESIGN":
          store.approveFrontDesign();
          break;
        case "SET_BACK_DESIGN_MODE":
          store.setBackDesignMode();
          break;
        case "GENERATE_START":
          store.generateStart(action.payload.currentStep);
          break;
        case "GENERATE_FRONT_SUCCESS":
          store.generateFrontSuccess(action.payload);
          break;
        case "GENERATE_BACK_SUCCESS":
          store.generateBackSuccess(action.payload);
          break;
        case "GENERATE_ERROR":
          store.generateError(action.payload);
          break;
        case "SET_GENERATION_PHASE":
          store.setGenerationPhase(action.payload);
          break;
        case "SET_PROGRESS":
          store.setProgress(action.payload);
          break;
        case "SET_GENERATION_TIMING":
          store.setGenerationTiming(action.payload);
          break;
        case "SET_GENERATION_BLOCKED":
          store.setGenerationBlocked(
            action.payload.blocked,
            action.payload.cooldownTime
          );
          break;
        case "SET_LOADING":
          store.setLoading(action.payload);
          break;
        case "SELECT_FRONT_VARIATION":
          store.selectFrontVariation(action.payload);
          break;
        case "SELECT_BACK_VARIATION":
          store.selectBackVariation(action.payload);
          break;
        case "SET_SELECTING_VARIATION":
          store.setSelectingVariation(action.payload);
          break;
        case "MODIFY_START":
          store.modifyStart();
          break;
        case "MODIFY_ERROR":
          store.modifyError(action.payload);
          break;
        case "SET_MODIFY_INPUT":
          store.setModifyInput(action.payload);
          break;
        case "SET_MODIFY_PROMPT":
          store.setModifyPrompt(action.payload);
          break;
        case "SET_IS_MODIFYING":
          store.setIsModifying(action.payload);
          break;
        case "SET_FULL_SIZE_IMAGE":
          store.setFullSizeImage(action.payload);
          break;
        case "SET_CURRENT_VIEW":
          store.setCurrentView(action.payload);
          break;
        case "SET_MODIFICATION_MODE":
          store.setModificationMode(action.payload);
          break;
        case "MODIFY_SUCCESS":
          store.modifySuccess(action.payload);
          break;
        case "MODIFY_FRONT_SUCCESS":
          store.modifyFrontSuccess(action.payload);
          break;
        case "MODIFY_BACK_SUCCESS":
          store.modifyBackSuccess(action.payload);
          break;
        case "SET_TICKET_ID":
          store.setTicketId(action.payload);
          break;
        case "SET_VARIATION_TICKET_ID":
          store.setVariationTicketId(
            action.payload.index,
            action.payload.ticketId
          );
          break;
        case "SUBMIT_START":
          store.submitStart();
          break;
        case "SUBMIT_SUCCESS":
          store.submitSuccess();
          break;
        case "SUBMIT_ERROR":
          store.submitError(action.payload);
          break;
        case "SHOW_CREDIT_MODAL":
          store.setShowCreditModal(action.payload);
          break;
        case "HIDE_CREDIT_MODAL":
          store.hideCreditModal();
          break;
        case "SHOW_CREDIT_CHECKOUT":
          store.setShowCreditCheckout(action.payload);
          break;
        case "HIDE_CREDIT_CHECKOUT":
          store.hideCreditCheckout();
          break;
        case "SHOW_CREDIT_SUCCESS":
          store.setShowCreditSuccess(action.payload);
          break;
        case "HIDE_CREDIT_SUCCESS":
          store.hideCreditSuccess();
          break;
        // Note: SET_CREDIT_BALANCE and SET_CREDIT_CHECK_LOADING removed - use useCredits() hook
        case "SET_CREDIT_MODAL_DISMISSED":
          store.setCreditModalDismissed(action.payload);
          break;
        case "SHOW_FEEDBACK_MODAL":
          store.setShowFeedbackModal();
          break;
        case "HIDE_FEEDBACK_MODAL":
          store.hideFeedbackModal();
          break;
        case "SET_ERROR":
          store.setError(action.payload);
          break;
        case "CLEAR_ERROR":
          store.clearError();
          break;
        case "CLEAR_WARNINGS":
          store.clearWarnings();
          break;
        case "RESTORE_IMAGE_STATE":
          store.restoreImageState(action.payload);
          break;
        case "RESTORE_VARIATIONS":
          store.restoreVariations(action.payload);
          break;
        case "RESET_ALL":
          store.resetAll();
          break;
        default:
          // Ignore unknown actions
          break;
      }
    },
    []
  );
  const actions = useCreatePageActions({ dispatch, state, isChatMode: true });
  const { isLoading: creditsLoading } = useCreditsContext();

  const actionsRef = useRef({
    updateFormData: actions.updateFormData,
    setStep: actions.setStep,
    setError: actions.setError,
    showCreditModal: actions.showCreditModal,
    generateImages: actions.generateImages,
    updateGeneratedTicket: actions.updateGeneratedTicket,
  });

  // Keep actionsRef in sync with actions
  // useLayoutEffect runs synchronously after render, before other effects
  useLayoutEffect(() => {
    actionsRef.current = {
      updateFormData: actions.updateFormData,
      setStep: actions.setStep,
      setError: actions.setError,
      showCreditModal: actions.showCreditModal,
      generateImages: actions.generateImages,
      updateGeneratedTicket: actions.updateGeneratedTicket,
    };
  });

  // Create stable actions object for effects using refs
  // This prevents useCreatePageEffects from re-running when actions change
  const memoizedActionsForEffects = useMemo(
    () => ({
      updateFormData: (
        ...args: Parameters<typeof actionsRef.current.updateFormData>
      ) => actionsRef.current.updateFormData(...args),
      setStep: (...args: Parameters<typeof actionsRef.current.setStep>) =>
        actionsRef.current.setStep(...args),
      setError: (...args: Parameters<typeof actionsRef.current.setError>) =>
        actionsRef.current.setError(...args),
      showCreditModal: (
        ...args: Parameters<typeof actionsRef.current.showCreditModal>
      ) => actionsRef.current.showCreditModal(...args),
      generateImages: (
        ...args: Parameters<typeof actionsRef.current.generateImages>
      ) => actionsRef.current.generateImages(...args),
      updateGeneratedTicket: (
        ...args: Parameters<typeof actionsRef.current.updateGeneratedTicket>
      ) => actionsRef.current.updateGeneratedTicket(...args),
    }),
    [] // Empty dependency array - refs are stable
  );

  const { restorationPending: formRestorationPending, retryTicketRestoration } =
    useCreatePageEffects({
      state,
      dispatch,
      actions: memoizedActionsForEffects,
      agentSessionRestorationPending,
    });
  // An explicit ticket/gallery target is authoritative and deliberately does
  // not restore a prior agent session. Do not let unrelated auth/session
  // hydration keep that target behind the startup shell.
  const restorationPending =
    formRestorationPending ||
    (!authoritativeRestoreTargetKey && agentSessionRestorationPending);
  // An exact My Dresses deep link is an ownership boundary. If it fails, keep
  // any prior canvas only as an internal recovery point and let the page render
  // a terminal restore surface instead of an active composer under the wrong
  // session URL.
  const sessionRestoreError =
    requestedSessionId && !agentSessionRestorationPending
      ? state.agent.exactSessionRestoreError
      : null;
  // On a cold mount the prior dress pointer can exist only in durable browser
  // storage. Preserve that verified UUID as the recovery destination when an
  // unrelated exact My Dresses link fails; "Start a new dress" would otherwise
  // clear the only pointer to still-recoverable work.
  const recoverySessionId =
    state.agent.sessionId ??
    (sessionRestoreError ? readStoredAgentSessionId() : null);
  const sessionRecoveryHref =
    sessionRestoreError &&
    recoverySessionId &&
    recoverySessionId !== requestedSessionId
      ? `/create?session_id=${encodeURIComponent(recoverySessionId)}`
      : null;
  const sessionSignInHref =
    sessionRestoreError === "Sign in to open this saved dress." &&
    requestedSessionId
      ? `/login?redirect=${encodeURIComponent(
          `/create?session_id=${requestedSessionId}`
        )}`
      : null;
  const sessionRestoreRetryable =
    sessionRestoreError === EXACT_SESSION_RESTORE_RETRY_MESSAGE;

  return {
    // State (mapped from new nested structure for backward compatibility)
    formData: state.form.data,
    currentStep: state.form.currentStep,
    totalSteps: state.form.totalSteps,
    isModificationMode: state.ui.modification.isModificationMode,
    hasPrompt: state.form.hasPrompt,
    loading: state.generation.progress.loading,
    isGenerationBlocked: state.generation.progress.isBlocked,
    generationCooldownTime: state.generation.progress.cooldownTime,
    imageUrl: state.generation.images.front.url,
    imagePath: state.generation.images.front.path,
    backImageUrl: state.generation.images.back.url,
    backImagePath: state.generation.images.back.path,
    originalImageUrl: state.generation.images.front.original.url,
    originalImagePath: state.generation.images.front.original.path,
    originalBackImageUrl: state.generation.images.back.original.url,
    originalBackImagePath: state.generation.images.back.original.path,
    // Variations state
    frontVariations:
      state.generation.images.variations.front.length > 0
        ? state.generation.images.variations.front
        : null,
    backVariations:
      state.generation.images.variations.back.length > 0
        ? state.generation.images.variations.back
        : null,
    selectedFrontVariationIndex: state.generation.images.selected.frontIndex,
    selectedBackVariationIndex: state.generation.images.selected.backIndex,
    ticketId: state.ticket.id,
    designSubmitted: state.ticket.designSubmitted,
    errorMsg: state.ticket.submissionError ?? state.generation.errors.message,
    progress: state.generation.progress.progress,
    hasFailedGeneration: state.generation.progress.hasFailedGeneration,
    showModifyInput: state.ui.modification.showInput,
    modifyPrompt: state.ui.modification.prompt,
    isModifying: state.ui.modification.isModifying,
    showFullSizeImage: state.ui.modals.fullSizeImage,
    currentView: state.ui.view,
    showCreditModal: state.ui.modals.credit.show,
    creditModalContext: state.ui.modals.credit.context,
    showCreditCheckout: state.ui.modals.credit.checkout,
    showCreditSuccess: state.ui.modals.credit.success,
    creditSuccessData: state.ui.modals.credit.successData,
    creditConfig: state.ui.modals.credit.config,
    showFeedbackModal: state.ui.modals.feedback,
    showQuoteAccountModal: state.ui.modals.quoteAccount,
    // Note: creditBalance and creditCheckLoading removed - use useCredits() hook directly
    showValidationError: state.form.showValidationError,
    generationPhase: state.generation.progress.phase,
    generationStartTime: state.generation.progress.startTime,
    estimatedTimeRemaining: state.generation.progress.estimatedTimeRemaining,
    warnings: state.generation.errors.warnings,
    partialFailure: state.generation.errors.partialFailure,
    isSelectingVariation: state.generation.images.isSelectingVariation,
    frontDesignLocked: state.form.frontDesignLocked,
    originalPresetType: state.ui.modification.originalPresetType,
    variationTicketIds: state.ticket.variationTicketIds,
    creditModalDismissed: state.ui.modals.credit.dismissed,
    creditPurchaseLoading: state.ui.modals.credit.purchaseLoading,
    creditsLoading, // Expose credits loading state for UI
    restorationPending,
    sessionRestoreError,
    sessionRestoreRetryable,
    sessionRecoveryHref,
    sessionSignInHref,
    retryTicketRestoration,

    // Actions
    updateFormData: actions.updateFormData,
    handleNext: actions.handleNext,
    handlePrevious: actions.handlePrevious,
    handleStartOver: actions.handleStartOver,
    setStep: actions.setStep,
    approveFrontDesign: actions.approveFrontDesign,
    generateBackOnly: actions.generateBackOnly,
    handleSubmit: actions.generateImages,
    handleModifyImage: actions.modifyImage,
    handleCreateTicket: actions.submitTicket,
    getPendingQuoteDesignSpec: actions.getPendingQuoteDesignSpec,
    getPendingQuoteScope: actions.getPendingQuoteScope,
    handleCreditModalClose: actions.hideCreditModal,
    handleCreditModalDismiss: actions.dismissCreditModal,
    handlePurchase: actions.purchaseCredits,
    handleCreateAccountAndBuy: actions.upgradeAndPurchaseCredits,
    handleCreateAccountAndQuote: actions.upgradeAndSubmitQuote,
    handleQuoteAccountModalDismiss: actions.hideQuoteAccountModal,
    handleCreditSuccess: actions.handleCreditSuccess,
    handleCreditSuccessClose: actions.hideCreditSuccess,
    setShowCreditCheckout: actions.hideCreditCheckout,
    setShowModifyInput: actions.setShowModifyInput,
    setModifyPrompt: actions.setModifyPrompt,
    setShowFullSizeImage: actions.setShowFullSizeImage,
    setCurrentView: actions.setCurrentView,
    setErrorMsg: actions.setError,
    clearWarnings: actions.clearWarnings,
    setShowCreditModal: actions.setShowCreditModal,
    setShowFeedbackModal: actions.setShowFeedbackModal,
    hideFeedbackModal: actions.hideFeedbackModal,
    setCreditConfig: () => {}, // This will be handled by the credit flow
    setShowValidationError: actions.setValidationError,
    // Variation selection handlers
    selectFrontVariation: actions.selectFrontVariation,
    selectBackVariation: actions.selectBackVariation,

    // Internal actions for effects
    updateGeneratedTicket: actions.updateGeneratedTicket,

    // Agent actions
    sendAgentMessage: actions.sendAgentMessage,
    processAgentResponse: actions.processAgentResponse,

    // Generation actions (exposed for direct use)
    generateImages: actions.generateImages,
  };
}
